// Reconnect and reload app when returning from background (Android/iOS)
function reconnectOnVisibilityChange() {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      // Phaser's built-in pauseOnBlur relies on window blur/focus, which mobile
      // browsers don't fire reliably when backgrounding a tab/PWA, so audio
      // keeps playing unless we pause it explicitly here.
      if (window.game && window.game.sound) {
        window.game.sound.pauseAll();
      }

      // When app goes to background, disconnect socket
      if (window.socket && window.socket.connected) {
        console.info("[CLIENT] App going to background, disconnecting socket");
        window.socket.disconnect();
      }
    } else if (document.visibilityState === "visible") {
      // When app returns to foreground, fully reload the page
      console.info("[CLIENT] App returning to foreground, reloading page");
      window.location.reload();
    }
  });
}

reconnectOnVisibilityChange();
// Fix TypeScript error for import.meta.env
declare global {
  interface ImportMetaEnv {
    DEV: boolean;
  }
  interface ImportMeta {
    env: ImportMetaEnv;
  }
  interface Window {
    deferredPrompt: any;
  }
}

// Initialize translations before scenes load
import { initI18n } from "./i18n/config";
import i18next from "i18next";

initI18n().catch((error) => {
  console.warn("Failed to initialize i18n, using English as fallback:", error);
});

// Expose i18next globally for debugging
(window as any).i18next = i18next;

import { LoadingScene } from "./scenes/loading-scene";
import { BaseScene } from "./scenes/base/base-scene";
import { LobbyScene } from "./scenes/lobby-scene";
import { NoInternetScene } from "./scenes/no-internet-scene";
import { MarketScene } from "./scenes/market-scene";
import Phaser from "phaser";
import { MainScene } from "./scenes/main-scene";
import {
  handleDeepLink,
  initializeDeepLinkListener,
} from "./utils/deep-link-handler";
/// <reference types="vite/client" />

import { isMobile, isTouchDevice } from "./utils/mobile-utils";

// Single config and import for LoginScene
const ASPECT_RATIO = 16 / 9;
let gameWidth = 640;
let gameHeight = 360;

if (!isTouchDevice()) {
  // Desktop and tablets use fixed dimensions
  gameWidth = 720;
  gameHeight = 405;
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  width: gameWidth,
  height: gameHeight,
  scene: [LoadingScene, BaseScene, LobbyScene, MarketScene, NoInternetScene],
  parent: "game",
  backgroundColor: "#181a20",
  audio: {
    disableWebAudio: false,
    noAudio: false,
  },
  render: {
    pixelArt: true,
    antialias: false,
    roundPixels: true,
  },
  scale: {
    mode: isMobile() ? Phaser.Scale.RESIZE : Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: gameWidth,
    height: gameHeight,
  },
};

const game = new Phaser.Game(config);
window.game = game;

// Unlock audio context on iOS/mobile devices
// iOS requires user interaction before playing audio
function unlockAudioContext() {
  const unlockAudio = () => {
    if (game.sound) {
      const soundManager = game.sound as Phaser.Sound.WebAudioSoundManager;
      if (soundManager.context && soundManager.context.state === "suspended") {
        soundManager.context.resume().then(() => {
          console.info("Audio context unlocked by user interaction");
        });
      }
    }
    // Remove listeners after first interaction
    document.removeEventListener("touchstart", unlockAudio);
    document.removeEventListener("touchend", unlockAudio);
    document.removeEventListener("click", unlockAudio);
  };

  // Listen for any user interaction to unlock audio
  document.addEventListener("touchstart", unlockAudio, { once: true });
  document.addEventListener("touchend", unlockAudio, { once: true });
  document.addEventListener("click", unlockAudio, { once: true });
}

// Initialize audio unlock on mobile
if (isTouchDevice()) {
  unlockAudioContext();
}

// Function to wait for fonts to load
function waitForFonts(): Promise<void> {
  return new Promise((resolve) => {
    // Check if document.fonts API is available
    if ("fonts" in document) {
      // Wait for the Anton font specifically
      document.fonts.ready.then(() => {
        // Double-check that Anton font is actually loaded
        const antonLoaded = document.fonts.check("16px Anton");
        if (antonLoaded) {
          resolve();
        } else {
          resolve();
        }
      });
    } else {
      // Fallback for browsers without Font Loading API
      // Wait a bit for fonts to load
      setTimeout(() => {
        resolve();
      }, 1000);
    }
  });
}

// Wait for fonts before starting the game
waitForFonts().then(async () => {
  // Check for deep link on app startup
  await handleDeepLink();

  // Initialize deep link listener for iOS
  initializeDeepLinkListener();

  game.scene.start("LoadingScene");
});

// Handle window resize for mobile devices
if (isTouchDevice()) {
  const resizeGame = () => {
    const newWidth = window.innerWidth;
    const newHeight = window.innerHeight;
    game.scale.resize(newWidth, newHeight);
  };

  window.addEventListener("resize", resizeGame);

  // Also handle orientation changes for mobile
  window.addEventListener("orientationchange", () => {
    setTimeout(resizeGame, 100);
  });

  // Handle visual viewport changes (for when mobile browser chrome appears/disappears)
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", resizeGame);
  }
}

// Helper to start MainScene and connect to server
export function startMainScene() {
  if (!window.socket) {
    console.error("[CLIENT] No socket found. Please connect to server first.");
    return;
  }

  // Remove existing MainScene if it exists to ensure fresh instance
  const existingMainScene = game.scene.getScene("MainScene");
  if (existingMainScene) {
    game.scene.remove("MainScene");
  }

  // Add and start a fresh MainScene instance
  game.scene.add("MainScene", MainScene, false);
  game.scene.start("MainScene");
}

// Helper to add BaseScene at runtime
export function addBaseSceneToGame() {
  if (!game.scene.getScene("BaseScene")) {
    game.scene.add("BaseScene", BaseScene, false);
  }
}
window.addBaseSceneToGame = addBaseSceneToGame;
